/**
 * Smart Greeting Helper for Pre-Approval Templates
 *
 * Provides consistent greeting formatting across PDF, preview, and email rendering.
 * Handles proper capitalization and title formatting.
 */

export interface BorrowerGreetingInfo {
  salutation?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Capitalize a name using boundary-aware title-casing.
 *
 * Only uppercases the first character of each whitespace-separated segment.
 * Never forces lowercase on trailing characters — preserves user-typed casing.
 *
 * Examples:
 *   "billy bob"   → "Billy Bob"
 *   "McDonald"    → "McDonald"
 *   "O'Brien"     → "O'Brien"
 *   "Smith-Jones"  → "Smith-Jones"
 *   "SMITH"       → "SMITH"
 */
export function capitalizeName(name: string | undefined): string {
  if (!name || name.trim() === '') {
    return '';
  }
  return name.trim()
    .split(/(\s+)/)
    .map(segment => {
      if (/^\s+$/.test(segment) || segment.length === 0) {
return segment;
}
      return segment.charAt(0).toUpperCase() + segment.slice(1);
    })
    .join('');
}

/**
 * Generate smart greeting for pre-approval templates
 *
 * Logic:
 * - If salutation (Mr./Ms./etc.) AND lastName exist → "Mr. Smith"
 * - Otherwise → "John" (properly capitalized first name)
 *
 * @param borrowerInfo - Object containing salutation, firstName, lastName
 * @returns Formatted greeting name (without "Dear")
 *
 * @example
 * formatSmartGreeting({ salutation: 'Mr.', lastName: 'Smith' }) → "Mr. Smith"
 * formatSmartGreeting({ firstName: 'TOny' }) → "Tony"
 * formatSmartGreeting({ salutation: 'none', firstName: 'john' }) → "John"
 */
export function formatSmartGreeting(borrowerInfo?: BorrowerGreetingInfo): string {
  if (!borrowerInfo) {
return '[Borrower Name]';
}

  const { salutation, firstName, lastName } = borrowerInfo;

  // Use formal greeting with title if available
  if (salutation && salutation !== 'none' && lastName) {
    // Capitalize lastName for consistency
    const capitalizedLastName = capitalizeName(lastName);
    return `${salutation} ${capitalizedLastName}`;
  }

  // Fallback to properly capitalized first name
  return capitalizeName(firstName) || '[Borrower Name]';
}

/**
 * Generate complete greeting sentence
 *
 * @example
 * formatGreetingSentence({ salutation: 'Mr.', lastName: 'Smith' }) → "Dear Mr. Smith,"
 */
export function formatGreetingSentence(borrowerInfo?: BorrowerGreetingInfo): string {
  const greetingName = formatSmartGreeting(borrowerInfo);
  return `Dear ${greetingName},`;
}

export function formatCombinedGreeting(
  borrowerInfo?: BorrowerGreetingInfo,
  coBorrowerInfo?: BorrowerGreetingInfo
): string {
  const primary = formatSmartGreeting(borrowerInfo);
  const secondary = formatSmartGreeting(coBorrowerInfo);

  if (secondary === '[Borrower Name]' || !secondary) {
    return primary;
  }

  return `${primary} and ${secondary}`;
}
